import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Send, Loader2, MessageCircle, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating, onRate, interactive = false, size = "h-5 w-5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          data-testid={interactive ? `star-${star}` : undefined}
          onClick={() => interactive && onRate?.(star)}
          className={interactive ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"}
          disabled={!interactive}
        >
          <Star
            className={`${size} transition-colors ${
              star <= rating
                ? "fill-amber-400 text-amber-400"
                : "fill-none text-stone-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const date = new Date(review.created_at).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      data-testid={`review-card-${review.id}`}
      className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_2px_12px_rgb(0,0,0,0.03)] transition-all hover:shadow-[0_4px_20px_rgb(0,0,0,0.06)]"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-body font-semibold text-[#78350F] text-sm">
            {review.name}
          </h4>
          <p className="font-body text-xs text-stone-400 mt-0.5">{date}</p>
        </div>
        <StarRating rating={review.rating} size="h-4 w-4" />
      </div>
      <p className="font-body text-sm text-stone-600 leading-relaxed">
        {review.comment}
      </p>
      {review.admin_response && (
        <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-100">
          <div className="flex items-center gap-2 mb-1.5">
            <MessageCircle className="h-3.5 w-3.5 text-[#EA580C]" />
            <span className="font-body text-xs font-semibold text-[#EA580C] uppercase tracking-wider">
              M.O.M Response
            </span>
          </div>
          <p className="font-body text-sm text-stone-600 leading-relaxed">
            {review.admin_response}
          </p>
        </div>
      )}
    </div>
  );
}

export default function ReviewSection() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 0, comment: "" });

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.comment || form.rating === 0) {
      toast.error("Please fill in your name, rating, and review");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you for your review!");
      setForm({ name: "", rating: 0, comment: "" });
      setShowForm(false);
      fetchReviews();
    } catch (err) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating =
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0;

  return (
    <section className="py-16 sm:py-20" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="font-body text-xs text-[#EA580C] tracking-[0.2em] uppercase mb-3 font-semibold">
            Customer Reviews
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#78350F] mb-4">
            What Our Customers Say
          </h2>
          {reviews.length > 0 && (
            <div className="flex items-center justify-center gap-3 mt-2">
              <StarRating rating={Math.round(Number(avgRating))} size="h-5 w-5" />
              <span className="font-heading text-2xl font-bold text-[#78350F]">
                {avgRating}
              </span>
              <span className="font-body text-sm text-stone-400">
                ({reviews.length} review{reviews.length !== 1 ? "s" : ""})
              </span>
            </div>
          )}
        </div>

        {/* Reviews Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#EA580C]" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <Quote className="h-10 w-10 text-stone-200 mx-auto mb-4" />
            <p className="font-body text-stone-500">
              No reviews yet. Be the first to share your experience!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        )}

        {/* Write Review */}
        <div className="text-center">
          {!showForm ? (
            <Button
              data-testid="write-review-btn"
              onClick={() => setShowForm(true)}
              className="rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold px-8 h-11 shadow-lg transition-all active:scale-95"
            >
              <Star className="mr-2 h-4 w-4" /> Write a Review
            </Button>
          ) : (
            <div className="max-w-md mx-auto bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_24px_rgb(0,0,0,0.05)] animate-scale-in">
              <h3 className="font-heading text-lg text-[#78350F] mb-4">
                Share Your Experience
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4 text-left">
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600">
                    Your Name
                  </Label>
                  <Input
                    data-testid="review-name-input"
                    placeholder="Enter your name"
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                    className="bg-white border-stone-200 focus:border-[#EA580C] focus:ring-[#EA580C]/20 rounded-xl h-10 font-body"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600">
                    Rating
                  </Label>
                  <StarRating
                    rating={form.rating}
                    onRate={(r) => setForm((p) => ({ ...p, rating: r }))}
                    interactive
                    size="h-7 w-7"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-body text-sm text-stone-600">
                    Your Review
                  </Label>
                  <textarea
                    data-testid="review-comment-input"
                    placeholder="Tell us about your experience..."
                    value={form.comment}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, comment: e.target.value }))
                    }
                    rows={3}
                    className="w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm font-body focus:outline-none focus:border-[#EA580C] focus:ring-2 focus:ring-[#EA580C]/20 resize-none"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    data-testid="cancel-review-btn"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setForm({ name: "", rating: 0, comment: "" });
                    }}
                    className="flex-1 rounded-full font-body"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-review-btn"
                    disabled={submitting || !form.name || !form.comment || form.rating === 0}
                    className="flex-1 rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold transition-all active:scale-95 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Submit
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
